import { useEffect, useRef, type ReactNode } from "react";
import { useSnackbar } from "notistack";import { pingApiHealth } from "api/apiConnectivity";
import { NetworkStatusModal } from "components/common/network/NetworkStatusModal";
import {
  selectNetworkStatus,
  useNetworkStatusStore,
  type NetworkStatus,
} from "components/store/networkStatusStore";

const POLL_ONLINE_MS = 30_000;
const POLL_OFFLINE_MS = 5_000;

function NetworkStatusMonitor() {
  const { enqueueSnackbar } = useSnackbar();
  const status = useNetworkStatusStore(selectNetworkStatus);
  const previousStatusRef = useRef<NetworkStatus | null>(null);
  const isBlocking =
    status === "offline" || status === "server-unreachable";

  useEffect(() => {
    if (!isBlocking) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isBlocking]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (delay: number) => {
      timer = setTimeout(async () => {
        if (cancelled) return;
        await pingApiHealth();
        if (cancelled) return;
        const next = selectNetworkStatus(useNetworkStatusStore.getState());
        schedule(next === "online" ? POLL_ONLINE_MS : POLL_OFFLINE_MS);
      }, delay);
    };

    const handleOnline = () => {
      useNetworkStatusStore.getState().setBrowserOnline(true);
      void pingApiHealth().then(() => {
        if (!cancelled) {
          schedule(
            selectNetworkStatus(useNetworkStatusStore.getState()) === "online"
              ? POLL_ONLINE_MS
              : POLL_OFFLINE_MS,
          );
        }
      });
    };

    const handleOffline = () => {
      useNetworkStatusStore.getState().setBrowserOnline(false);
      useNetworkStatusStore.getState().setBackendReachable(false);
      useNetworkStatusStore.getState().setChecking(false);
      useNetworkStatusStore.getState().markChecked();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    void pingApiHealth().then(() => {
      if (!cancelled) {
        schedule(
          selectNetworkStatus(useNetworkStatusStore.getState()) === "online"
            ? POLL_ONLINE_MS
            : POLL_OFFLINE_MS,
        );
      }
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (status === "checking") return;

    const previous = previousStatusRef.current;
    previousStatusRef.current = status;
    if (!previous || previous === status) return;

    if (status === "online" && previous !== "online") {
      enqueueSnackbar("Connection restored. You can continue working.", {
        variant: "success",
        preventDuplicate: true,
      });
    }
  }, [enqueueSnackbar, status]);

  return <NetworkStatusModal status={status} />;
}

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <NetworkStatusMonitor />
    </>
  );
}